import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { LINK_SOURCE_LABELS, type ContactDto, type LinkedContactDto } from '@lawyer/shared'
import { useState } from 'react'
import { useContactOptions } from '../../hooks/useLookups'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { EmptyState, ErrorAlert } from '../common/Feedback'
import { ContactDialog } from './ContactDialog'

type Props = {
  contacts: LinkedContactDto[]
  /** Present on a case: rows inherited from the project are read-only here. */
  explainInheritance?: boolean
  busy?: boolean
  error?: unknown
  onAttach: (contactId: string, role: string) => void
  onRoleChange: (contactId: string, role: string) => void
  onDetach: (contactId: string) => void
}

/** The synthetic "create «what you typed»" row of the picker. */
type ContactOption = ContactDto & { isNew?: boolean }
const filterContacts = createFilterOptions<ContactOption>()

function AttachDialog({
  open,
  existingIds,
  onClose,
  onAttach,
}: {
  open: boolean
  existingIds: string[]
  onClose: () => void
  onAttach: (contactId: string, role: string) => void
}) {
  const contacts = useContactOptions()
  const [selected, setSelected] = useState<ContactOption | null>(null)
  const [role, setRole] = useState('')
  /** The name typed into the picker; non-null while the create dialog is open. */
  const [createName, setCreateName] = useState<string | null>(null)

  const options: ContactOption[] = (contacts.data ?? []).filter(
    (contact) => !existingIds.includes(contact.id)
  )

  const close = () => {
    setSelected(null)
    setRole('')
    onClose()
  }

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>שיוך איש קשר</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              options={options}
              value={selected}
              loading={contacts.isPending}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(all, state) => {
                const filtered = filterContacts(all, state)
                const typed = state.inputValue.trim()
                if (typed && !all.some((option) => option.name === typed)) {
                  filtered.push({
                    id: '',
                    name: typed,
                    email: null,
                    phone: null,
                    createdAt: '',
                    updatedAt: '',
                    isNew: true,
                  })
                }
                return filtered
              }}
              renderOption={({ key, ...props }, option) => (
                <li key={key} {...props}>
                  {option.isNew ? `יצירת איש קשר חדש: ${option.name}` : option.name}
                </li>
              )}
              onChange={(_event, option) => {
                if (option?.isNew) setCreateName(option.name)
                else setSelected(option)
              }}
              noOptionsText="אין אנשי קשר פנויים"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="איש קשר"
                  autoFocus
                  helperText="אפשר להקליד שם חדש וליצור אותו כאן"
                />
              )}
            />
            <TextField
              label="תפקיד"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="עו״ד מלווה, מתווך, שמאי…"
            />
            <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateName('')}>
              יצירת איש קשר חדש
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>ביטול</Button>
          <Button
            variant="contained"
            disabled={!selected}
            onClick={() => {
              if (!selected) return
              onAttach(selected.id, role)
              close()
            }}
          >
            שיוך
          </Button>
        </DialogActions>
      </Dialog>

      <ContactDialog
        open={createName !== null}
        defaultName={createName ?? ''}
        onClose={() => setCreateName(null)}
        onSaved={(contact) => setSelected(contact)}
      />
    </>
  )
}

export function ContactsPanel({
  contacts,
  explainInheritance = false,
  busy,
  error,
  onAttach,
  onRoleChange,
  onDetach,
}: Props) {
  const [attachOpen, setAttachOpen] = useState(false)
  const [pendingDetach, setPendingDetach] = useState<LinkedContactDto | null>(null)
  const [roleDrafts, setRoleDrafts] = useState<Record<string, string>>({})

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        {explainInheritance ? (
          <Typography variant="caption" color="text.secondary">
            אנשי קשר עם התגית ״מהפרויקט״ מנוהלים בכרטיס הפרויקט.
          </Typography>
        ) : (
          <span />
        )}
        <Button startIcon={<AddIcon />} onClick={() => setAttachOpen(true)}>
          שיוך איש קשר
        </Button>
      </Stack>

      {error ? <ErrorAlert error={error} /> : null}

      {contacts.length === 0 ? (
        <EmptyState title="אין אנשי קשר" />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>שם</TableCell>
              <TableCell>דואר אלקטרוני</TableCell>
              <TableCell>טלפון</TableCell>
              <TableCell sx={{ width: 220 }}>תפקיד</TableCell>
              <TableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => {
              const inherited = contact.source === 'project' && !contact.removable
              return (
                <TableRow key={contact.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{contact.name}</Typography>
                      {explainInheritance && contact.source === 'project' ? (
                        <Chip size="small" variant="outlined" label={LINK_SOURCE_LABELS.project} />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {contact.email ? (
                      <Link href={`mailto:${contact.email}`} underline="hover">
                        {contact.email}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{contact.phone || '—'}</TableCell>
                  <TableCell>
                    {inherited ? (
                      <Typography variant="body2" color="text.secondary">
                        {contact.role || '—'}
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        value={roleDrafts[contact.id] ?? contact.role ?? ''}
                        onChange={(e) =>
                          setRoleDrafts((prev) => ({ ...prev, [contact.id]: e.target.value }))
                        }
                        onBlur={(e) => {
                          if ((contact.role ?? '') !== e.target.value) {
                            onRoleChange(contact.id, e.target.value)
                          }
                        }}
                        disabled={busy}
                      />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip
                      title={inherited ? 'הסרה מתבצעת בכרטיס הפרויקט' : 'הסרת השיוך'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={inherited || busy}
                          onClick={() => setPendingDetach(contact)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <AttachDialog
        open={attachOpen}
        existingIds={contacts.map((contact) => contact.id)}
        onClose={() => setAttachOpen(false)}
        onAttach={onAttach}
      />

      <ConfirmDialog
        open={Boolean(pendingDetach)}
        title="הסרת איש קשר"
        message="השיוך יוסר. כרטיס איש הקשר עצמו יישאר במאגר."
        confirmLabel="הסרה"
        destructive={false}
        busy={busy}
        onConfirm={() => {
          if (pendingDetach) onDetach(pendingDetach.id)
          setPendingDetach(null)
        }}
        onClose={() => setPendingDetach(null)}
      />
    </>
  )
}
